import "@/helpers/customFilterServices";

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import type { DataTableFilterMeta } from "primereact/datatable";
import { Column } from "primereact/column";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { SplitButton } from "primereact/splitbutton";
import { Skeleton } from "primereact/skeleton";
import { FilterMatchMode } from "primereact/api";
import { StatusPill } from "sj-ui-toolkit/src/components";
import { DeveloperProfileConstants } from "sj-ui-toolkit/src/constants/developerProfile";
import { hasEuAiActBundle } from "sj-ui-toolkit/src/constants/bundles";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { getDirectoryClient, getApiTokensClient } from "sj-ui-toolkit/src/client";
import { initCable, UNLOCK_ALL_USERS_CHANNEL } from "sj-ui-toolkit/src/client/actionCable";
import {
  mapIsAdmin,
  mapIsArchived,
  impersonateAsync,
  getCurrentSelectedTenant,
} from "sj-ui-toolkit/src/utils/user";
import { capitalize } from "lodash";

import { AppContext } from "@/context/AppContext";
import { STATUS_TYPES } from "@/constants/users";
import type { UserStatus } from "@/constants/users";
import {
  loadLicensesAsync,
  loadAdminsAsync,
  unlockUserAsync,
  checkIfUserLockedAsync,
  addLicenseAsync,
} from "@/api/users";
import type { AdminUser, Lesson, Belt } from "@/api/users";
import { useAdminUsageData } from "@/hooks/useDashboardData";
import { skeletonRows, withSkeleton } from "@/helpers/tableBody";
import {
  experienceSortFunction,
  nullLastStringSort,
  toolingSortFunction,
  aiStackCategorySortFunction,
  additionalLanguagesSortFunction,
  devProfileJobFunction,
  devProfileLanguage,
  devProfileExperience,
  devProfileExpertise,
  devProfileTopics,
  devProfileAiStackCategory,
  devProfileAdditionalLanguages,
  normalizeAiStack,
  AI_STACK_COLUMNS,
  FLAG_GATED_PROFILE_FIELDS,
} from "@/helpers/developerProfile";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AddUsersDialog } from "@/pages/users/AddUsersDialog";
import { RequestSeatsDialog } from "@/pages/users/RequestSeatsDialog";
import { EditUserDialog } from "@/pages/users/EditUserDialog";
import {
  textFilterElement,
  multiSelectFilterElement,
  dropdownFilterElement,
  triStateBooleanFilterElement,
} from "@/helpers/tableFilters";
import type { SelectOption } from "@/helpers/tableFilters";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils/themeClasses";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UserTenant {
  tenant: string;
  deactivated?: boolean;
  roles?: string[];
}

export interface UserData {
  directoryUuid: string;
  id?: string;
  email?: string;
  name?: { givenName?: string; familyName?: string };
  attributes?: {
    employeeNumber?: string;
    jobRole?: string;
    title?: string;
    country?: string;
    company?: string;
    businessUnit?: string;
    department?: string;
    teamName?: string;
    managersEmail?: string;
    managersName?: string;
    securityChampion?: boolean;
  };
  developerProfile?: {
    jobFunction?: string;
    language?: string;
    experience?: string;
    expertise?: string;
    topics?: string[];
    aiStack?: Record<string, string[] | undefined>;
    usesAi?: boolean;
    additionalLanguages?: string[];
  };
  tenants?: UserTenant[];
  status?: UserStatus;
  admin?: boolean;
  licensed?: boolean;
  lastSeen?: string;
  allowUnlock?: boolean;
  receiveActivityNotifications?: boolean;
  lessons?: Lesson[];
  passedBelts?: Belt[];
}

// ---------------------------------------------------------------------------
// Initial filters
// ---------------------------------------------------------------------------

const INITIAL_FILTERS: DataTableFilterMeta = {
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  "name.givenName": { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  "name.familyName": { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  email: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  status: { value: [STATUS_TYPES.LICENSED], matchMode: FilterMatchMode.IN },
  admin: { value: null, matchMode: FilterMatchMode.EQUALS },
  "attributes.employeeNumber": { value: null, matchMode: FilterMatchMode.CONTAINS },
  "attributes.jobRole": { value: null, matchMode: FilterMatchMode.CONTAINS },
  "attributes.title": { value: null, matchMode: FilterMatchMode.CONTAINS },
  "attributes.country": { value: null, matchMode: FilterMatchMode.CONTAINS },
  "attributes.company": { value: null, matchMode: FilterMatchMode.EQUALS },
  "attributes.businessUnit": { value: null, matchMode: FilterMatchMode.CONTAINS },
  "attributes.department": { value: null, matchMode: FilterMatchMode.CONTAINS },
  "attributes.teamName": { value: null, matchMode: FilterMatchMode.CONTAINS },
  "attributes.managersEmail": { value: null, matchMode: FilterMatchMode.CONTAINS },
  "attributes.managersName": { value: null, matchMode: FilterMatchMode.CONTAINS },
  "attributes.securityChampion": { value: null, matchMode: FilterMatchMode.EQUALS },
  "developerProfile.jobFunction": { value: null, matchMode: FilterMatchMode.EQUALS },
  "developerProfile.language": { value: null, matchMode: "inCaseInsensitive" as FilterMatchMode },
  "developerProfile.experience": { value: null, matchMode: FilterMatchMode.EQUALS },
  "developerProfile.expertise": { value: null, matchMode: FilterMatchMode.EQUALS },
  "developerProfile.topics": { value: null, matchMode: "custom_array_contains" as FilterMatchMode },
};

// Merged into INITIAL_FILTERS only when the developerProfileAiUpdates flag is on
const AI_PROFILE_FILTERS: DataTableFilterMeta = {
  ...Object.fromEntries(
    AI_STACK_COLUMNS.map(c => [
      c.field,
      { value: null, matchMode: "custom_array_contains" as FilterMatchMode },
    ])
  ),
  "developerProfile.additionalLanguages": {
    value: null,
    matchMode: "custom_array_contains" as FilterMatchMode,
  },
};

const GLOBAL_FILTER_FIELDS = [
  "name.givenName",
  "name.familyName",
  "email",
  "attributes.employeeNumber",
  "attributes.jobRole",
  "attributes.title",
  "attributes.company",
  "attributes.country",
  "attributes.businessUnit",
  "attributes.department",
  "attributes.teamName",
  "attributes.managersEmail",
  "attributes.managersName",
  "developerProfile.jobFunction",
  "developerProfile.language",
  "developerProfile.experience",
  "developerProfile.expertise",
];

// ---------------------------------------------------------------------------
// Module-level body templates (no component state dependencies)
// ---------------------------------------------------------------------------

const emailBody = withSkeleton<UserData>(row => {
  if (row.directoryUuid && row.id) {
    const detailPath = AdminAppRoutes.routes.users.routes.id.route.replace(
      ":user_id",
      row.directoryUuid
    );
    return (
      <Link
        className={`${themeClasses.textPrimaryDarker} [.p-highlight_&]:!text-white`}
        to={`${detailPath}?userId=${row.id}`}
      >
        {row.email}
      </Link>
    );
  }
  return <span>{row.email}</span>;
}, "18rem");

const givenNameBody = withSkeleton<UserData>(row => row.name?.givenName ?? "--", "8rem");
const familyNameBody = withSkeleton<UserData>(row => row.name?.familyName ?? "--", "8rem");

const jobFunctionBody = withSkeleton<UserData>(row => devProfileJobFunction(row.developerProfile));
const languageBody = withSkeleton<UserData>(row => devProfileLanguage(row.developerProfile));
const experienceBody = withSkeleton<UserData>(row => devProfileExperience(row.developerProfile));
const expertiseBody = withSkeleton<UserData>(row => devProfileExpertise(row.developerProfile));
const toolingBody = withSkeleton<UserData>(row => devProfileTopics(row.developerProfile));
const aiStackBodies = Object.fromEntries(
  AI_STACK_COLUMNS.map(c => [
    c.key,
    withSkeleton<UserData>(row => devProfileAiStackCategory(row.developerProfile, c.key)),
  ])
);
const additionalLanguagesBody = withSkeleton<UserData>(row =>
  devProfileAdditionalLanguages(row.developerProfile)
);

const lastLoginBody = withSkeleton<UserData>(row => {
  if (!row.lastSeen) return "N/A";
  const d = new Date(row.lastSeen);
  return `${d.toLocaleDateString("en-US")} ${d.toLocaleTimeString("en-US")}`;
});

const securityChampionBody = withSkeleton<UserData>(row =>
  row.attributes?.securityChampion === true
    ? "Yes"
    : row.attributes?.securityChampion === false
      ? "No"
      : ""
);

// Filter elements with no runtime dependencies (stable across renders)
const searchFilterEl = textFilterElement("Search...");
const emailFilterEl = textFilterElement("Search email...");

// Static data derived from imports (no component state dependencies)
const TECHNICAL_EXPERTISE_OPTIONS: SelectOption[] =
  DeveloperProfileConstants.OPTIONS.technicalExpertise.map(opt => ({
    ...opt,
    label: capitalize(opt.value.replace(/_/g, " ")),
  }));

const STATUS_OPTIONS: SelectOption[] = [
  { label: "Licensed", value: STATUS_TYPES.LICENSED },
  { label: "Archived", value: STATUS_TYPES.ARCHIVED },
  { label: "Unlicensed", value: STATUS_TYPES.UNLICENSED },
];

const ADMIN_OPTIONS: SelectOption[] = [
  { label: "Admin", value: "true" },
  { label: "Learner", value: "false" },
];

const CONFIGURABLE_COLUMNS = [
  { field: "name.givenName", header: "First name" },
  { field: "name.familyName", header: "Last name" },
  { field: "developerProfile.jobFunction", header: "Job Function" },
  { field: "developerProfile.language", header: "Programming Language" },
  { field: "attributes.employeeNumber", header: "Employee #" },
  { field: "attributes.jobRole", header: "Job role" },
  { field: "attributes.title", header: "Title" },
  { field: "attributes.company", header: "Company" },
  { field: "attributes.securityChampion", header: "Security Champion" },
  { field: "developerProfile.experience", header: "Development Experience" },
  { field: "developerProfile.expertise", header: "Technical Expertise" },
  { field: "developerProfile.topics", header: "Tooling" },
  ...AI_STACK_COLUMNS.map(c => ({ field: c.field, header: c.header })),
  { field: "developerProfile.additionalLanguages", header: "Additional Languages" },
  { field: "attributes.country", header: "Country" },
  { field: "attributes.businessUnit", header: "Business unit" },
  { field: "attributes.department", header: "Department" },
  { field: "attributes.teamName", header: "Team" },
  { field: "attributes.managersEmail", header: "Manager's email" },
  { field: "attributes.managersName", header: "Manager's name" },
  { field: "status", header: "Status" },
  { field: "admin", header: "Role" },
  { field: "lastSeen", header: "Last Login" },
];

// ---------------------------------------------------------------------------
// Main Users page
// ---------------------------------------------------------------------------

export default function Users() {
  useBreadcrumb({ label: "Users", route: "/admin/users", display: "none", resetTrail: true });

  const currentTenantName = getCurrentSelectedTenant() ?? "";
  const stateKey = `users-table-${currentTenantName}`;
  const { isSjAdmin, contentBundles } = useUserContext();
  const {
    state: { viewOnly },
  } = useContext(AppContext);
  const { showToast } = useToast();
  const dtRef = useRef<DataTable<UserData[]>>(null);
  const { developerProfileAiUpdates } = useFlags();
  const { data: usageData } = useAdminUsageData({});

  // EU AI Act tenants that have used every purchased license see a "request more seats"
  // form instead of the normal add-users dialog.
  const currentLicense = usageData?.currentLicense;
  const atSeatLimit =
    typeof currentLicense?.userCount === "number" &&
    typeof currentLicense?.currentUserCount === "number" &&
    currentLicense.currentUserCount >= currentLicense.userCount;
  const showSeatRequest = hasEuAiActBundle(contentBundles) && atSeatLimit;

  const [users, setUsers] = useState<UserData[] | "loading">("loading");
  const [selectedUsers, setSelectedUsers] = useState<UserData[]>([]);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [showAddUsers, setShowAddUsers] = useState(false);
  const [showRequestSeats, setShowRequestSeats] = useState(false);
  const [seatRequestInitial, setSeatRequestInitial] = useState<number | undefined>(undefined);
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const editingUserRef = useRef<UserData | null>(null);
  const [scimEnabled, setScimEnabled] = useState(false);
  const [notificationList, setNotificationList] = useState<AdminUser[]>([]);
  const [filters, setFilters] = useState<DataTableFilterMeta>(() => {
    const base: DataTableFilterMeta = developerProfileAiUpdates
      ? { ...INITIAL_FILTERS, ...AI_PROFILE_FILTERS }
      : { ...INITIAL_FILTERS };
    try {
      const stored = localStorage.getItem(stateKey);
      if (stored) {
        const parsed = JSON.parse(stored) as { filters?: DataTableFilterMeta };
        if (parsed.filters) {
          return { ...base, ...parsed.filters };
        }
      }
    } catch {
      /* ignore */
    }
    return base;
  });
  const [globalFilterValue, setGlobalFilterValue] = useState(() => {
    try {
      const stored = localStorage.getItem(stateKey);
      if (stored) {
        const parsed = JSON.parse(stored) as { filters?: { global?: { value?: string | null } } };
        return parsed.filters?.global?.value ?? "";
      }
    } catch {
      /* ignore */
    }
    return "";
  });

  const { user: currentUser } = useUserContext();

  const storedRows = useMemo(() => {
    try {
      const stored = localStorage.getItem(stateKey);
      if (stored) {
        const parsed = JSON.parse(stored) as { rows?: number };
        return parsed.rows ?? 10;
      }
    } catch {
      /* ignore */
    }
    return 10;
  }, []);
  const skeletonData = useMemo(() => skeletonRows<UserData>(storedRows), [storedRows]);

  // Keep ref in sync so load() can read the current editing user without
  // capturing stale state (adding editingUser to load's dep array would
  // trigger a full reload on every setEditingUser call).
  useEffect(() => {
    editingUserRef.current = editingUser;
  }, [editingUser]);

  // Clear row selection state on mount. PrimeReact DataTable persists selection to
  // stateStorage and restores it via onSelectionChange on the first re-render
  // after mount. Removing it from localStorage here prevents that restoration,
  // so that the table always starts with no pre-selected rows.
  useEffect(() => {
    setSelectedUsers([]);
    try {
      const stored = localStorage.getItem(stateKey);
      if (stored) {
        const state = JSON.parse(stored) as Record<string, unknown>;
        if ("selection" in state) {
          delete state.selection;
          localStorage.setItem(stateKey, JSON.stringify(state));
        }
      }
    } catch {
      /* do nothing */
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const load = useCallback(async () => {
    setUsers("loading");
    const svcDirectoryClient = getDirectoryClient();
    try {
      const [directoryRes, licensesRes, adminsRes] = await Promise.all([
        svcDirectoryClient.getUsersForTenant(currentTenantName, { includeArchived: true }),
        loadLicensesAsync(),
        loadAdminsAsync(),
      ]);

      const licensesMap = new Map(licensesRes.map(r => [r.directoryUuid, r]));

      const directoryUsers = (directoryRes.data?.users ?? []) as UserData[];
      const merged: UserData[] = directoryUsers.map(du => {
        const ru = licensesMap.get(du.directoryUuid ?? "") ?? {};
        const status: UserStatus = mapIsArchived(du, ru, currentTenantName)
          ? STATUS_TYPES.ARCHIVED
          : !(ru as { licensed?: boolean }).licensed
            ? STATUS_TYPES.UNLICENSED
            : STATUS_TYPES.LICENSED;
        const admin = mapIsAdmin(du, currentTenantName) ?? false;

        const dp = du.developerProfile
          ? {
              ...du.developerProfile,
              language: du.developerProfile.language?.trim().toLowerCase(),
              topics: du.developerProfile.topics?.map((t: string) => t.trim().toLowerCase()),
              ...(developerProfileAiUpdates && {
                aiStack: normalizeAiStack(du.developerProfile),
                additionalLanguages: du.developerProfile.additionalLanguages?.map((t: string) =>
                  t.trim().toLowerCase()
                ),
              }),
            }
          : undefined;

        return { ...du, ...ru, status, admin, developerProfile: dp } as UserData;
      });

      setUsers(merged);
      setNotificationList(adminsRes.admins);

      if (editingUserRef.current) {
        const refreshed = merged.find(
          u => u.directoryUuid === editingUserRef.current!.directoryUuid
        );
        if (refreshed) setEditingUser({ ...refreshed });
      }
    } catch (e) {
      console.error(e);
      showToast({ severity: "error", detail: "Failed to load users" });
    }
  }, [currentTenantName, showToast, developerProfileAiUpdates]);

  useEffect(() => {
    void load();
  }, [load]);

  // WebSocket for bulk unlock (isSjAdmin only)
  useEffect(() => {
    if (!isSjAdmin) return;
    const sub = initCable(UNLOCK_ALL_USERS_CHANNEL, (data: { status: string; message: string }) => {
      showToast({
        severity: data.status === "success" ? "success" : "error",
        detail: data.message,
      });
      if (data.status === "success") void load();
    });
    return () => sub.unsubscribe();
  }, [isSjAdmin, load, showToast]);

  // SCIM check
  useEffect(() => {
    const checkScim = async () => {
      try {
        const { data, error } = await getApiTokensClient().listApiTokensWithEntitlements(["scim"]);
        setScimEnabled(!error && !!data?.apiTokens?.length);
      } catch {
        setScimEnabled(false);
      }
    };
    void checkScim();
  }, []);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const toggleEdit = (directoryUuid: string) => {
    const user = (users as UserData[]).find(u => u.directoryUuid === directoryUuid);
    if (user) {
      setEditingUser({ ...user });
      setShowEditUser(true);
    }
  };

  const handleUnlock = async (id: string) => {
    try {
      await unlockUserAsync(id);
      showToast({ severity: "success", detail: "User unlocked successfully" });
      void load();
    } catch {
      console.error("Failed to unlock user");
    }
  };

  const checkIfUnlocked = async (rowData: UserData) => {
    try {
      const resp = await checkIfUserLockedAsync(rowData.directoryUuid);
      setUsers(prev =>
        prev === "loading"
          ? prev
          : prev.map(u =>
              u.directoryUuid === rowData.directoryUuid
                ? { ...u, allowUnlock: !resp.isUnlocked }
                : u
            )
      );
    } catch {
      showToast({ severity: "error", detail: "Failed to check unlock status" });
    }
  };

  const handleArchive = async (isUnarchive: boolean) => {
    const target = isUnarchive ? STATUS_TYPES.LICENSED : STATUS_TYPES.ARCHIVED;
    const toUpdate = selectedUsers.filter(u => u.status !== target);
    if (!toUpdate.length) return;
    setIsActionLoading(true);
    const uuids = toUpdate.map(u => u.directoryUuid);
    const svcDirectoryClient = getDirectoryClient();
    try {
      const response = isUnarchive
        ? await svcDirectoryClient.unarchiveUsers(currentTenantName, uuids)
        : await svcDirectoryClient.archiveUsers(currentTenantName, uuids);

      if (response.error) {
        showToast({ severity: "error", detail: "Failed to update users" });
      } else {
        const { succeeded = [], failed = [] } =
          (response.data as { succeeded?: unknown[]; failed?: unknown[] }) ?? {};
        if (failed.length > 0) {
          showToast({
            severity: "warn",
            summary: "Partial Success",
            detail: `${succeeded.length} updated, ${failed.length} failed`,
          });
        } else {
          showToast({ severity: "success", detail: "Users updated successfully" });
        }
      }
      setSelectedUsers([]);
      void load();
    } catch {
      showToast({ severity: "error", detail: "Failed to update users" });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleLicense = async () => {
    // Capped EU AI Act tenants can't gain seats through bulk licensing (the BE rejects it);
    // route them to the request-seats form instead, prefilled with the count they selected.
    if (showSeatRequest) {
      const unlicensedCount = selectedUsers.filter(u => !u.licensed).length;
      setSeatRequestInitial(unlicensedCount || undefined);
      setShowRequestSeats(true);
      return;
    }
    const allLicensed = selectedUsers.every(u => u.licensed);
    if (allLicensed) {
      showToast({ severity: "error", detail: "All selected users are already licensed" });
      return;
    }
    setIsActionLoading(true);
    try {
      await Promise.all(
        selectedUsers.filter(u => !u.licensed && u.id).map(u => addLicenseAsync(u.id!))
      );
      showToast({ severity: "success", detail: "Users updated successfully" });
      setSelectedUsers([]);
      void load();
    } catch (e) {
      showToast({
        severity: "error",
        detail:
          (e as { response?: { data?: { errors?: string } } })?.response?.data?.errors ??
          "Failed to add licenses",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Column computed values
  // ---------------------------------------------------------------------------

  const isLoading = users === "loading";
  const usersList = isLoading ? [] : (users as UserData[]);

  const canBulkAddLicense = selectedUsers.every(u => !u.licensed);
  const canBulkArchive = selectedUsers.every(u => u.status === STATUS_TYPES.LICENSED);
  const canBulkUnarchive = selectedUsers.every(u => u.status === STATUS_TYPES.ARCHIVED);

  const companyOptions = useMemo<SelectOption[]>(
    () =>
      [...new Set(usersList.map(u => u.attributes?.company).filter(Boolean))]
        .sort((a, b) => a!.localeCompare(b!))
        .map(c => ({ label: c!, value: c! })),
    [usersList]
  );

  // ---------------------------------------------------------------------------
  // Column definitions
  // ---------------------------------------------------------------------------

  // Memoized filter elements
  const statusFilterEl = useMemo(
    () => multiSelectFilterElement(STATUS_OPTIONS, "Select Status"),
    []
  );
  const adminFilterEl = useMemo(() => dropdownFilterElement(ADMIN_OPTIONS, "All roles"), []);
  const jobFunctionFilterEl = useMemo(
    () =>
      dropdownFilterElement(
        (developerProfileAiUpdates
          ? DeveloperProfileConstants.OPTIONS.jobRolesNext
          : DeveloperProfileConstants.OPTIONS.jobRoles) as SelectOption[],
        "All job functions"
      ),
    [developerProfileAiUpdates]
  );
  const languageFilterEl = useMemo(
    () =>
      multiSelectFilterElement(
        DeveloperProfileConstants.OPTIONS.programmingLanguages as SelectOption[],
        "All languages"
      ),
    []
  );
  const experienceFilterEl = useMemo(
    () =>
      dropdownFilterElement(
        DeveloperProfileConstants.OPTIONS.experienceLevels as SelectOption[],
        "All levels"
      ),
    []
  );
  const expertiseFilterEl = useMemo(
    () => dropdownFilterElement(TECHNICAL_EXPERTISE_OPTIONS as SelectOption[], "All expertise"),
    []
  );
  const toolingFilterEl = useMemo(
    () =>
      dropdownFilterElement(
        DeveloperProfileConstants.OPTIONS.architectureTools as SelectOption[],
        "All tools"
      ),
    []
  );
  const aiStackFilterEls = useMemo(
    () =>
      Object.fromEntries(
        AI_STACK_COLUMNS.map(c => [
          c.key,
          dropdownFilterElement(c.filterOptions as SelectOption[], `All ${c.filterNoun}`),
        ])
      ),
    []
  );
  const additionalLanguagesFilterEl = useMemo(
    () =>
      dropdownFilterElement(
        DeveloperProfileConstants.OPTIONS.additionalLanguages as SelectOption[],
        "All languages"
      ),
    []
  );
  const companyFilterEl = useMemo(
    () => dropdownFilterElement(companyOptions, "All companies"),
    [companyOptions]
  );

  // ---------------------------------------------------------------------------
  // Cell renderers
  // ---------------------------------------------------------------------------

  const statusBodyFn = useMemo(
    // eslint-disable-next-line react/display-name
    () => (row: UserData | null) => {
      if (row == null) return <Skeleton height="1.5rem" width="6rem" borderRadius="16px" />;
      return (
        <StatusPill status={row.status ?? "unlicensed"} statusType="user" variant="outlined" />
      );
    },
    []
  );

  const adminBodyFn = useMemo(
    // eslint-disable-next-line react/display-name
    () => (row: UserData | null) => {
      if (row == null) return <Skeleton width="4rem" />;
      return (
        <StatusPill
          status={row.admin ? "admin" : "learner"}
          statusType="user"
          variant="filled"
          displayText={row.admin ? "Admin" : "Learner"}
        />
      );
    },
    []
  );

  const actionsBody = (row: UserData | null) => {
    if (row == null) return null;
    if (viewOnly) return null;
    if (isSjAdmin) {
      return (
        <SplitButton
          size="small"
          label="Edit"
          disabled={isLoading || isActionLoading}
          onClick={() => toggleEdit(row.directoryUuid)}
          model={[
            {
              label: "Impersonate",
              icon: "pi pi-eye",
              command: () => void impersonateAsync(row.email ?? ""),
            },
            {
              label: row.allowUnlock ? "Unlock" : "Already Unlocked",
              icon: `pi ${row.allowUnlock ? "pi-lock-open" : "pi-lock"}`,
              disabled: !row.allowUnlock || viewOnly,
              command: () => void handleUnlock(row.id ?? ""),
            },
          ]}
          onShow={() => void checkIfUnlocked(row)}
        />
      );
    }
    return (
      <Button
        size="small"
        label="Edit"
        text
        disabled={isLoading || isActionLoading || viewOnly}
        onClick={() => toggleEdit(row.directoryUuid)}
      />
    );
  };

  // ---------------------------------------------------------------------------
  // Header
  // ---------------------------------------------------------------------------

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGlobalFilterValue(value);
    setFilters(prev => ({ ...prev, global: { value, matchMode: FilterMatchMode.CONTAINS } }));
  };

  const configurableColumns = developerProfileAiUpdates
    ? CONFIGURABLE_COLUMNS
    : CONFIGURABLE_COLUMNS.filter(c => !FLAG_GATED_PROFILE_FIELDS.has(c.field));

  const [visibleConfigColumns, setVisibleConfigColumns] = useState<string[]>([
    "name.givenName",
    "name.familyName",
    "developerProfile.jobFunction",
    "developerProfile.language",
    "status",
    "admin",
    "lastSeen",
  ]);

  const tableHeader = (
    <div className="grid xl:grid-cols-2 gap-4">
      <div className="flex gap-4">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            id="global-search-input"
            data-testid="global-search-input"
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Keyword Search"
            autoComplete="off"
            aria-label="Search users"
          />
        </IconField>
        <MultiSelect
          id="column-select"
          value={visibleConfigColumns}
          onChange={e => setVisibleConfigColumns(e.value as string[])}
          options={configurableColumns}
          optionLabel="header"
          optionValue="field"
          placeholder={
            visibleConfigColumns.length === 0
              ? "Select columns"
              : visibleConfigColumns.length < 3
                ? visibleConfigColumns
                    .map(f => CONFIGURABLE_COLUMNS.find(c => c.field === f)?.header ?? f)
                    .join(", ")
                : `${visibleConfigColumns.length} columns selected`
          }
          fixedPlaceholder
          filter
          className="w-58"
          aria-label="Toggle columns"
        />
      </div>
      <div className="flex flex-row-reverse gap-4">
        {selectedUsers.length > 0 ? (
          <>
            <Button
              id="license-button"
              data-testid="license-button"
              icon="pi pi-check"
              disabled={isLoading || isActionLoading || !canBulkAddLicense || viewOnly}
              severity="success"
              className={!canBulkAddLicense ? "opacity-50" : ""}
              onClick={() => void handleLicense()}
              tooltip={`Add License${selectedUsers.length > 1 ? "s" : ""}`}
              tooltipOptions={{ position: "top" }}
              aria-label={`Add License${selectedUsers.length > 1 ? "s" : ""}`}
            />
            <Button
              id="archive-button"
              data-testid="archive-button"
              icon="pi pi-user-minus"
              disabled={isLoading || isActionLoading || !canBulkArchive || viewOnly}
              severity="danger"
              className={!canBulkArchive ? "opacity-50" : ""}
              onClick={() => void handleArchive(false)}
              tooltip={`Archive User${selectedUsers.length > 1 ? "s" : ""}`}
              tooltipOptions={{ position: "top" }}
              aria-label={`Archive User${selectedUsers.length > 1 ? "s" : ""}`}
            />
            <Button
              id="unarchive-button"
              data-testid="unarchive-button"
              icon="pi pi-user-plus"
              disabled={isLoading || isActionLoading || !canBulkUnarchive || viewOnly}
              className={!canBulkUnarchive ? "opacity-50" : ""}
              onClick={() => void handleArchive(true)}
              tooltip={`Unarchive User${selectedUsers.length > 1 ? "s" : ""}`}
              tooltipOptions={{ position: "top" }}
              aria-label={`Unarchive User${selectedUsers.length > 1 ? "s" : ""}`}
            />
          </>
        ) : (
          <Button
            data-testid="add-users-button"
            label="Add Users"
            icon="pi pi-user-plus"
            onClick={() => {
              if (showSeatRequest) {
                setSeatRequestInitial(undefined);
                setShowRequestSeats(true);
              } else {
                setShowAddUsers(true);
              }
            }}
            disabled={isLoading || isActionLoading || viewOnly}
          />
        )}
        <Button
          label="Export data"
          icon="pi pi-download"
          disabled={isLoading || isActionLoading || viewOnly}
          onClick={() => dtRef.current?.exportCSV({ selectionOnly: false })}
          tooltip="Export data as CSV"
          tooltipOptions={{ position: "left" }}
        />
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      <h1 className="atlas-h1 mb-4">Users</h1>
      <Card>
        <DataTable<UserData[]>
          ref={dtRef}
          value={(isLoading ? skeletonData : usersList) as unknown as UserData[]}
          loading={false}
          selection={selectedUsers}
          onSelectionChange={e => setSelectedUsers(e.value as UserData[])}
          selectionMode="multiple"
          metaKeySelection={false}
          filters={isLoading ? {} : filters}
          onFilter={isLoading ? undefined : e => setFilters(e.filters)}
          globalFilterFields={GLOBAL_FILTER_FIELDS}
          filterDisplay="menu"
          paginator
          rows={storedRows}
          rowsPerPageOptions={[10, 25, 50, 100]}
          stateStorage="local"
          stateKey={stateKey}
          tableClassName="dt-responsive-text"
          removableSort
          scrollable
          header={tableHeader}
          emptyMessage="No users found."
          aria-label="Users table"
        >
          <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />

          {/* Email (static, always visible) */}
          <Column
            field="email"
            header="Email"
            sortable
            filter
            filterElement={emailFilterEl}
            showFilterMatchModes={false}
            body={emailBody}
            style={{ minWidth: "18rem" }}
          />

          {/* Configurable columns */}
          {visibleConfigColumns.includes("name.givenName") && (
            <Column
              field="name.givenName"
              header="First name"
              sortable
              filter
              filterElement={searchFilterEl}
              showFilterMatchModes={false}
              body={givenNameBody}
              style={{ minWidth: "12rem" }}
            />
          )}
          {visibleConfigColumns.includes("name.familyName") && (
            <Column
              field="name.familyName"
              header="Last name"
              sortable
              filter
              filterElement={searchFilterEl}
              showFilterMatchModes={false}
              body={familyNameBody}
              style={{ minWidth: "12rem" }}
            />
          )}
          {visibleConfigColumns.includes("developerProfile.jobFunction") && (
            <Column
              field="developerProfile.jobFunction"
              header="Job Function"
              sortable
              sortFunction={nullLastStringSort("developerProfile.jobFunction")}
              filter
              filterElement={jobFunctionFilterEl}
              showFilterMatchModes={false}
              body={jobFunctionBody}
              style={{ minWidth: "12rem" }}
            />
          )}
          {visibleConfigColumns.includes("developerProfile.language") && (
            <Column
              field="developerProfile.language"
              header="Programming Language"
              sortable
              sortFunction={nullLastStringSort("developerProfile.language")}
              filter
              filterElement={languageFilterEl}
              showFilterMatchModes={false}
              body={languageBody}
              style={{ minWidth: "18rem" }}
            />
          )}
          {visibleConfigColumns.includes("attributes.employeeNumber") && (
            <Column
              field="attributes.employeeNumber"
              header="Employee #"
              sortable
              filter
              filterElement={searchFilterEl}
              showFilterMatchModes={false}
            />
          )}
          {visibleConfigColumns.includes("attributes.jobRole") && (
            <Column
              field="attributes.jobRole"
              header="Job role"
              sortable
              filter
              filterElement={searchFilterEl}
              showFilterMatchModes={false}
            />
          )}
          {visibleConfigColumns.includes("attributes.title") && (
            <Column
              field="attributes.title"
              header="Title"
              sortable
              filter
              filterElement={searchFilterEl}
              showFilterMatchModes={false}
            />
          )}
          {visibleConfigColumns.includes("attributes.company") && (
            <Column
              field="attributes.company"
              header="Company"
              sortable
              filter
              filterElement={companyFilterEl}
              showFilterMatchModes={false}
            />
          )}
          {visibleConfigColumns.includes("attributes.securityChampion") && (
            <Column
              field="attributes.securityChampion"
              header="Security Champion"
              sortable
              filter
              filterElement={triStateBooleanFilterElement}
              showFilterMatchModes={false}
              body={securityChampionBody}
            />
          )}
          {visibleConfigColumns.includes("developerProfile.experience") && (
            <Column
              field="developerProfile.experience"
              header="Development Experience"
              sortable
              sortFunction={experienceSortFunction}
              filter
              filterElement={experienceFilterEl}
              showFilterMatchModes={false}
              body={experienceBody}
            />
          )}
          {visibleConfigColumns.includes("developerProfile.expertise") && (
            <Column
              field="developerProfile.expertise"
              header="Technical Expertise"
              sortable
              sortFunction={nullLastStringSort("developerProfile.expertise")}
              filter
              filterElement={expertiseFilterEl}
              showFilterMatchModes={false}
              body={expertiseBody}
            />
          )}
          {visibleConfigColumns.includes("developerProfile.topics") && (
            <Column
              field="developerProfile.topics"
              header="Tooling"
              sortable
              sortFunction={toolingSortFunction}
              filter
              filterElement={toolingFilterEl}
              showFilterMatchModes={false}
              body={toolingBody}
            />
          )}
          {developerProfileAiUpdates &&
            AI_STACK_COLUMNS.map(
              c =>
                visibleConfigColumns.includes(c.field) && (
                  <Column
                    key={c.field}
                    field={c.field}
                    header={c.header}
                    sortable
                    sortFunction={aiStackCategorySortFunction(c.key)}
                    filter
                    filterElement={aiStackFilterEls[c.key]}
                    showFilterMatchModes={false}
                    body={aiStackBodies[c.key]}
                    style={{ minWidth: "12rem" }}
                  />
                )
            )}
          {developerProfileAiUpdates &&
            visibleConfigColumns.includes("developerProfile.additionalLanguages") && (
              <Column
                field="developerProfile.additionalLanguages"
                header="Additional Languages"
                sortable
                sortFunction={additionalLanguagesSortFunction}
                filter
                filterElement={additionalLanguagesFilterEl}
                showFilterMatchModes={false}
                body={additionalLanguagesBody}
              />
            )}
          {visibleConfigColumns.includes("attributes.country") && (
            <Column
              field="attributes.country"
              header="Country"
              sortable
              filter
              filterElement={searchFilterEl}
              showFilterMatchModes={false}
            />
          )}
          {visibleConfigColumns.includes("attributes.businessUnit") && (
            <Column
              field="attributes.businessUnit"
              header="Business unit"
              sortable
              filter
              filterElement={searchFilterEl}
              showFilterMatchModes={false}
            />
          )}
          {visibleConfigColumns.includes("attributes.department") && (
            <Column
              field="attributes.department"
              header="Department"
              sortable
              filter
              filterElement={searchFilterEl}
              showFilterMatchModes={false}
            />
          )}
          {visibleConfigColumns.includes("attributes.teamName") && (
            <Column
              field="attributes.teamName"
              header="Team"
              sortable
              filter
              filterElement={searchFilterEl}
              showFilterMatchModes={false}
            />
          )}
          {visibleConfigColumns.includes("attributes.managersEmail") && (
            <Column
              field="attributes.managersEmail"
              header="Manager's email"
              sortable
              filter
              filterElement={searchFilterEl}
              showFilterMatchModes={false}
            />
          )}
          {visibleConfigColumns.includes("attributes.managersName") && (
            <Column
              field="attributes.managersName"
              header="Manager's name"
              sortable
              filter
              filterElement={searchFilterEl}
              showFilterMatchModes={false}
            />
          )}
          {visibleConfigColumns.includes("status") && (
            <Column
              field="status"
              header="Status"
              sortable
              filter
              filterElement={statusFilterEl}
              showFilterMatchModes={false}
              body={statusBodyFn}
            />
          )}
          {visibleConfigColumns.includes("admin") && (
            <Column
              field="admin"
              header="Role"
              sortable
              filter
              filterElement={adminFilterEl}
              showFilterMatchModes={false}
              body={adminBodyFn}
            />
          )}
          {visibleConfigColumns.includes("lastSeen") && (
            <Column
              field="lastSeen"
              header="Last Login"
              sortable
              body={lastLoginBody}
              style={{ minWidth: "12rem" }}
            />
          )}

          {/* Actions (frozen right) */}
          <Column
            header="Actions"
            body={actionsBody}
            frozen
            className={`${themeClasses.bgElevation1} z-10, sticky`}
            headerClassName={`${themeClasses.bgElevation1} z-10, sticky`}
            alignFrozen="right"
            // style={{ position: "sticky", right: 0, zIndex: 1 }}
          />
        </DataTable>
      </Card>

      <AddUsersDialog
        visible={showAddUsers}
        onHide={() => setShowAddUsers(false)}
        currentTenantName={currentTenantName}
        onSuccess={() => void load()}
        showToast={showToast}
      />

      <RequestSeatsDialog
        visible={showRequestSeats}
        onHide={() => setShowRequestSeats(false)}
        showToast={showToast}
        purchasedCount={currentLicense?.userCount}
        initialSeatCount={seatRequestInitial}
      />

      {editingUser && (
        <EditUserDialog
          visible={showEditUser}
          user={editingUser}
          scimEnabled={scimEnabled}
          notificationSetting={
            notificationList.find(u => u.directoryUuid === editingUser.directoryUuid)
              ?.notificationSetting ?? false
          }
          currentTenantName={currentTenantName}
          currentUserDirectoryUuid={
            (currentUser as unknown as { directoryUuid?: string })?.directoryUuid ?? undefined
          }
          onHide={() => {
            setShowEditUser(false);
            setEditingUser(null);
          }}
          onSuccess={() => void load()}
          showToast={showToast}
        />
      )}
    </>
  );
}
